import { useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { ThemeToggle } from "./ThemeToggle";
import { useAuthStore, AuthModal, EmailSignupModal } from "@/features/auth";

const FULL_WIDTH_ROUTES = ["/"];

export function Shell() {
  const user = useAuthStore((s) => s.user);
  const emailUser = useAuthStore((s) => s.emailUser);
  const checkSession = useAuthStore((s) => s.checkSession);
  const openModal = useAuthStore((s) => s.openModal);
  const openEmailModal = useAuthStore((s) => s.openEmailModal);
  const signOut = useAuthStore((s) => s.signOut);
  const isSignedUp = !!(user || emailUser);
  const location = useLocation();
  const isHome = location.pathname === "/about";
  const isFullWidth = FULL_WIDTH_ROUTES.includes(location.pathname);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  return (
    <div className="min-h-screen" style={{ background: "#0f172a" }}>
      <nav className="sticky top-0 z-50 border-b border-slate-700/60" style={{ background: "rgba(15,23,42,0.8)", backdropFilter: "blur(8px)" }}>
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="text-lg font-bold text-white hover:text-indigo-400 transition-colors">
              Glossika
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            {user ? (
              <div className="flex items-center gap-3">
                <Link
                  to="/preferences"
                  className="text-sm text-slate-400 hover:text-slate-300 transition-colors hidden sm:inline"
                >
                  {user.email}
                </Link>
                <button
                  onClick={signOut}
                  className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            ) : emailUser ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-400 hidden sm:inline">{emailUser}</span>
                <button
                  onClick={openModal}
                  className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  Sign In
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={openModal}
                  className="text-sm text-slate-400 hover:text-slate-300 transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={openEmailModal}
                  className="text-sm px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-semibold transition-colors shadow-lg shadow-indigo-500/25"
                >
                  Get Started
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {isFullWidth ? (
        <Outlet />
      ) : (
        <main className="max-w-4xl mx-auto px-4 py-8">
          <Outlet />
        </main>
      )}

      {isHome && (
        <section className="border-t border-slate-800" style={{ background: "#0c1120" }}>
          <div className="mx-auto max-w-6xl px-6 py-24">
            <h2 className="text-center text-3xl font-bold text-white sm:text-4xl">
              Part of the Glossika ecosystem
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-center text-slate-400">
              A complete language learning system. Each product focuses on a different skill.
            </p>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <EcoCard
                name="Dialogs"
                description="Immersive conversations in 100+ languages. Listen, read, and role-play your way to fluency."
                tag="You are here"
                highlight
              />
              <EcoCard
                name="Glossika Mass Sentences"
                description="The original Glossika method. Thousands of sentences with audio for mass immersion."
                tag="Sentences"
                href="https://ai.glossika.com"
                appLinks={[
                  { label: "iOS", href: "https://apps.apple.com/tw/app/glossika-language-learning/id1597902556?l=en-GB" },
                  { label: "Android", href: "https://play.google.com/store/apps/details?id=com.glossika.ai" },
                ]}
              />
              <EcoCard
                name="Viva Video"
                description="Video-based lessons with native speakers. Watch, listen, and learn through real conversations."
                tag="Video lessons"
                appLinks={[
                  { label: "iOS", href: "https://apps.apple.com/us/app/glossika-real-life-english/id6667095609" },
                  { label: "Android", href: "https://play.google.com/store/apps/details?id=com.glossika.video" },
                ]}
              />
              <EcoCard
                name="Email Learning"
                description="Receive dialogs, grammar tips, vocabulary, and drills in your target language delivered regularly to your inbox."
                tag="Email course"
              />
              <EcoCard
                name="Glossika Premium"
                description="One subscription unlocks unlimited access across the entire Glossika suite of products."
                tag="All access"
              />
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8" style={{ background: "#0a0e1a" }}>
        <div className="max-w-5xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between text-sm text-slate-500">
          <span>&copy; 2026 Glossika Pte. Ltd., Singapore</span>
          <div className="flex gap-6 mt-4 sm:mt-0">
            <a href="https://glossika.com" className="hover:text-slate-300" target="_blank" rel="noopener noreferrer">Glossika.com</a>
            <a href="https://glossika.com/terms" className="hover:text-slate-300" target="_blank" rel="noopener noreferrer">Terms</a>
            <a href="https://glossika.com/privacy" className="hover:text-slate-300" target="_blank" rel="noopener noreferrer">Privacy</a>
          </div>
        </div>
      </footer>

      <AuthModal />
      <EmailSignupModal />
      <audio id="audio-element" preload="auto" />
    </div>
  );
}

function EcoCard({ name, description, tag, highlight, href, appLinks }: {
  name: string;
  description: string;
  tag: string;
  highlight?: boolean;
  href?: string;
  appLinks?: { label: string; href: string }[];
}) {
  return (
    <div className={`rounded-xl border p-6 ${highlight ? "border-indigo-500/40 bg-indigo-950/20" : "border-slate-800 bg-slate-900/50"}`}>
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-sm font-semibold text-white">{name}</h3>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${highlight ? "bg-indigo-600/30 text-indigo-300" : "bg-slate-800 text-slate-500"}`}>
          {tag}
        </span>
      </div>
      <p className="text-sm text-slate-400 leading-relaxed">{description}</p>
      {href && (
        <a href={href} className="mt-3 inline-block text-xs text-indigo-400 hover:text-indigo-300" target="_blank" rel="noopener noreferrer">
          Visit &rarr;
        </a>
      )}
      {appLinks && (
        <div className="mt-3 flex gap-2">
          {appLinks.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-300 hover:border-slate-500 hover:text-white"
              target="_blank"
              rel="noopener noreferrer"
            >
              {l.label === "iOS" ? (
                <svg className="h-3.5 w-3.5" viewBox="0 0 384 512" fill="currentColor">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                </svg>
              ) : (
                <svg className="h-3.5 w-3.5" viewBox="0 0 512 512" fill="currentColor">
                  <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
                </svg>
              )}
              {l.label === "iOS" ? "App Store" : "Google Play"}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
